import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Paper,
  Typography,
  InputAdornment,
  ThemeProvider,
  createTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import { 
  collection, 
  query, 
  getDocs, 
  orderBy, 
  where, 
  addDoc, 
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db, getNextPoNumber, addOrderToDb } from '../firebase';
import { 
  ORDER_STATUSES,
  LINE_STATUSES,
  LINE_TYPES,
  SIZES,
  sortSizes
} from '../firebase/schema';
import LineItem2 from '../components/LineItem2';
import AddCustomer from '../components/AddCustomer';
import { useFacility } from '../context/FacilityContext';
import { FACILITIES, resolveFacilityId } from '../utils/facilities';
import FeesModal from '../components/FeesModal';
import { EMPLOYEE_ROLES } from '../constants/employeeRoles';
import SuccessAnimation from '../components/SuccessAnimation';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { getPersistableMockupUrl } from '../utils/fileHelpers';
import { enrichCustomerDocForCreate } from '../utils/customerPortal';
import { computeSalesTax, sumTaxableFeesAmount, resolveTaxExempt, resolveOrderTaxExemptFlag } from '../utils/orderSalesTax';
import { computeUpchargeQuantity } from '../utils/orderTotals';
import { registerDesignMockupFromLine } from '../utils/designMockupIndex';

// Theme configuration
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#98FB98'
    },
    background: {
      default: '#1C1C1C',
      paper: '#2C2C2C'
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)'
    }
  },
  typography: {
    fontFamily: '"Montserrat", sans-serif',
    button: {
      textTransform: 'uppercase',
      fontWeight: 600
    }
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small'
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#363636',
            '&:hover': {
              backgroundColor: '#404040'
            },
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)'
            },
            '&:hover fieldset': {
              borderColor: '#98FB98'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#98FB98'
            }
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: '#98FB98'
            }
          },
          '& input': {
            color: 'white',
            '&[type=number]': {
              '-moz-appearance': 'textfield',
              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0
              }
            }
          }
        }
      }
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: '#98FB98'
            },
            transform: 'translate(14px, 9px) scale(1)',
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)'
            }
          }
        }
      }
    },
    MuiSelect: {
      defaultProps: {
        size: 'small'
      },
      styleOverrides: {
        root: {
          backgroundColor: '#363636',
          height: '40px',
          '&:hover': {
            backgroundColor: '#404040'
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.1)'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#98FB98'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#98FB98'
          }
        }
      }
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: '#363636'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#98FB98 !important',
            color: '#000000',
            fontWeight: 600
          },
          '&:hover': {
            backgroundColor: '#98FB98',
            color: '#000000'
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          textTransform: 'uppercase',
          fontWeight: 600
        },
        contained: {
          backgroundColor: '#98FB98',
          color: '#000000',
          '&:hover': {
            backgroundColor: '#7FE87F'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#2C2C2C',
          borderRadius: 0
        }
      }
    }
  }
});

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  padding: theme.spacing(3),
  paddingTop: `calc(60px + ${theme.spacing(3)})`, // Navbar height (60px) + padding
  paddingBottom: '220px', // Increased space for fixed footer
  position: 'relative',
  overflowY: 'auto'
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  display: 'grid',
  gridTemplateColumns: '2fr 0.7fr 0.7fr 2fr',
  gap: theme.spacing(2),
  alignItems: 'center',
  position: 'sticky',
  top: `calc(60px + ${theme.spacing(3)})`, // Match PageContainer paddingTop
  zIndex: 10,
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const LinesSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  // Remove maxHeight to allow natural scrolling with page padding
  '&::-webkit-scrollbar': {
    width: '8px'
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#4a4a4a',
    '&:hover': {
      background: '#5a5a5a'
    }
  }
}));

const FooterSection = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2),
  display: 'flex',
  gap: '12px',
  alignItems: 'stretch',
  zIndex: 100,
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

const FooterBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '12px',
  background: 'rgba(255, 255, 255, 0.02)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  minWidth: '120px',
}));

const NotesFooterBox = styled(FooterBox)({
  flex: 2,
  minWidth: '200px',
});

const UpchargeFooterBox = styled(FooterBox)({
  flex: 1,
});

const FeesFooterBox = styled(FooterBox)({
  flex: 1,
  '& .header': {
    padding: '4px',
    fontWeight: 600,
    fontSize: '0.85em',
  },
  '& .summary': {
    fontSize: '0.8em',
    color: 'rgba(255, 255, 255, 0.6)',
    flex: 1,
  },
  '& .fee-line': {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0',
  },
  '& .fee-type': {
    color: 'rgba(255, 255, 255, 0.8)',
  },
  '& .fee-amount': {
    color: 'var(--al-primary)',
    fontWeight: 600,
  },
  '& .instructions': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '6px',
    paddingTop: '6px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .instruction-tag': {
    background: 'var(--al-primary)',
    color: '#000',
    padding: '2px 6px',
    fontSize: '0.7em',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
});

const TotalsFooterBox = styled(FooterBox)({
  flex: 1,
  background: '#000',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  '& .header': {
    padding: '4px',
    fontWeight: 700,
    fontSize: '0.9em',
    color: 'white',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
});

const FooterRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  '& .label': {
    minWidth: '45px',
    width: '45px',
    textAlign: 'right',
    fontSize: '0.85em',
    flexShrink: 0,
    color: 'white',
    fontWeight: 600,
  },
  '& input': {
    background: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: 'var(--al-primary)',
    padding: '6px 8px',
    flex: 1,
    minWidth: '60px',
    fontFamily: '"Montserrat", sans-serif',
    fontWeight: 700,
  },
});

const EditFeesBtn = styled('button')(({ theme }) => ({
  background: 'transparent',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: 'white',
  padding: '8px 16px',
  cursor: 'pointer',
  fontFamily: '"Montserrat", sans-serif',
  textTransform: 'uppercase',
  fontSize: '0.75em',
  marginTop: 'auto',
  '&:hover': {
    borderColor: 'var(--al-primary)',
    color: 'var(--al-primary)',
  },
}));

const CustomerSearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%'
}));

const SearchResults = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  maxHeight: '200px',
  overflowY: 'auto',
  zIndex: 1000,
  marginTop: '2px'
}));

const SearchResultItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.black
  }
}));

const NotesField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    height: '150px',
    '& textarea': {
      height: '100% !important'
    }
  }
}));

const FeeGrid = styled(Grid)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(2),
  '& .MuiPaper-root': {
    padding: theme.spacing(1.5),
    height: '100%'
  },
  '& .MuiFormControl-root': {
    marginBottom: theme.spacing(0.5)
  },
  '& .MuiInputBase-root': {
    height: '32px'
  },
  '& .MuiGrid-container': {
    gap: theme.spacing(1)
  }
}));

const TotalsDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1)
}));

// Helper functions
const formatMinutes = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const calculate2XLPlus = (lines) => {
  return lines.reduce((total, line) => total + computeUpchargeQuantity(line), 0);
};

const calculateTotalPieces = (lines) => {
  return lines.reduce((total, line) => {
    const linePieces = Object.values(line.sizes || {})
      .reduce((sum, qty) => sum + (Number(qty) || 0), 0);
    return total + linePieces;
  }, 0);
};

const calculatePrintMinutes = (line) => {
  const quantity = Object.values(line.sizes || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
  const inkCount = (line.frontInkColors?.length || 0) + 
                  (line.backInkColors?.length || 0) + 
                  (line.otherInkColors?.length || 0);
  
  // Base setup time (15 minutes per color)
  const setupTime = inkCount * 15;
  
  // Print time (assume 240 pieces per hour = 4 pieces per minute)
  const printTime = quantity / 4;
  
  return setupTime + printTime;
};

const calculatePrintRevenue = (line) => {
  const quantity = Object.values(line.sizes || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
  const price = Number(line.price) || 0;
  const cogs = Number(line.cogs) || 0;
  
  if (quantity <= 0 || price <= 0) return 0;
  
  const revenuePerPiece = Math.max(0, price - cogs);
  return quantity * revenuePerPiece;
};

const calculatePressRate = (revenue, minutes) => {
  if (!revenue || !minutes || minutes <= 0) return 0;
  const hours = minutes / 60;
  return revenue / hours;
};

// Helper function to check if any instructions are active
const hasActiveInstructions = (instructions) => {
  if (!instructions) return false;
  return instructions.shipping || instructions.sorting || instructions.folding ||
         instructions.bagging || instructions.fulfillment || instructions.neckTag ||
         instructions.taxExempt || instructions.designWorkOnly || instructions.doNotBill ||
         instructions.followUpEmail || instructions.instagramWorthy || instructions.providedGoods ||
         instructions.sample ||
         !!instructions.other;
};

const calculatePricing = (lines, orderData, fees = [], customerTaxExempt = false) => {
  const lineTotals = lines.map(line => {
    // Process sizes based on type
    let processedSizes = {};
    
    // Handle Design Only lines
    if (line.type === LINE_TYPES.DESIGN_ONLY) {
      processedSizes = {
        'Design': Number(line.sizes?.Design || 0)
      };
    } else {
      // Handle regular print types
      switch(line.sizeType) {
        case 'youth':
          processedSizes = {
            'YXS': Number(line.sizes?.YXS || 0),
            'YS': Number(line.sizes?.YS || 0),
            'YM': Number(line.sizes?.YM || 0),
            'YL': Number(line.sizes?.YL || 0),
            'YXL': Number(line.sizes?.YXL || 0)
          };
          break;
        case 'ladies':
          processedSizes = {
            'XS': Number(line.sizes?.XS || 0),
            'S': Number(line.sizes?.S || 0),
            'M': Number(line.sizes?.M || 0),
            'L': Number(line.sizes?.L || 0),
            'XL': Number(line.sizes?.XL || 0),
            '2XL': Number(line.sizes?.['2XL'] || 0),
            '3XL': Number(line.sizes?.['3XL'] || 0)
          };
          break;
        default: // unisex/adult
          processedSizes = {
            'XS': Number(line.sizes?.XS || 0),
            'S': Number(line.sizes?.S || 0),
            'M': Number(line.sizes?.M || 0),
            'L': Number(line.sizes?.L || 0),
            'XL': Number(line.sizes?.XL || 0),
            '2XL': Number(line.sizes?.['2XL'] || 0),
            '3XL': Number(line.sizes?.['3XL'] || 0),
            '4XL': Number(line.sizes?.['4XL'] || 0),
            '5XL': Number(line.sizes?.['5XL'] || 0),
            '6XL': Number(line.sizes?.['6XL'] || 0),
            '7XL': Number(line.sizes?.['7XL'] || 0)
          };
      }
    }

    const totalQuantity = Object.values(processedSizes)
      .reduce((sum, qty) => sum + (Number(qty) || 0), 0);
    
    const upchargeQuantity = computeUpchargeQuantity({ ...line, sizes: processedSizes });
    
    const price = parseFloat(line.price) || 0;
    const cogs = parseFloat(line.cogs) || 0;
    const upchargeAmount = (orderData.upchargePrice === '' || orderData.upchargePrice == null || Number.isNaN(parseFloat(orderData.upchargePrice))) ? 2 : parseFloat(orderData.upchargePrice);
    
    return {
      quantity: totalQuantity,
      linePrice: (totalQuantity * price) + (upchargeQuantity * upchargeAmount),
      lineCOGS: totalQuantity * cogs,
      upchargeQuantity
    };
  });

  // Calculate totals
  const totalQuantity = lineTotals.reduce((sum, line) => sum + line.quantity, 0);
  const upchargeAmount = (orderData.upchargePrice === '' || orderData.upchargePrice == null || Number.isNaN(parseFloat(orderData.upchargePrice))) ? 2 : parseFloat(orderData.upchargePrice);
  const totalUpcharges = lineTotals.reduce((sum, line) => sum + (line.upchargeQuantity * upchargeAmount), 0);
  const subtotal = lineTotals.reduce((sum, line) => sum + line.linePrice, 0);
  const totalCOGS = lineTotals.reduce((sum, line) => sum + line.lineCOGS, 0);

  // Calculate fees
  const feesTotal = (Array.isArray(fees) ? fees : []).reduce((sum, fee) => {
    const quantity = parseFloat(fee?.quantity) || 0;
    const price = parseFloat(fee?.price) || 0;
    return sum + (quantity * price);
  }, 0);

  // Calculate subtotal before discount
  const subtotalBeforeDiscount = subtotal + feesTotal;

  // Calculate discount
  let discountAmount = 0;
  if (orderData.discount && orderData.discount.amount) {
    if (orderData.discount.type === 'percent') {
      discountAmount = subtotalBeforeDiscount * (parseFloat(orderData.discount.amount) / 100);
    } else {
      discountAmount = parseFloat(orderData.discount.amount) || 0;
    }
  }

  const taxableFeesTotal = sumTaxableFeesAmount(fees);
  const salesTax = computeSalesTax({
    subtotal,
    feesTotal,
    taxableFeesTotal,
    discountAmount,
    isTaxExempt: resolveTaxExempt({
      order: orderData,
      customer: customerTaxExempt ? { taxExempt: true } : null,
    }),
  });

  // Calculate final totals
  const orderTotal = subtotalBeforeDiscount - discountAmount;
  const cogsPercentage = orderTotal > 0 ? ((totalCOGS / orderTotal) * 100) : 0;

  // Calculate print minutes and revenue
  const printMinutes = lines.reduce((total, line) => total + calculatePrintMinutes(line), 0);
  const printRevenue = lines.reduce((total, line) => total + calculatePrintRevenue(line), 0);
  const pressRate = calculatePressRate(printRevenue, printMinutes);

  return {
    subtotal: Number(subtotal.toFixed(2)),
    feesTotal: Number(feesTotal.toFixed(2)),
    discountAmount: Number(discountAmount.toFixed(2)),
    orderTotal: Number(orderTotal.toFixed(2)),
    cogsTotal: Number(totalCOGS.toFixed(2)),
    cogsPercentage: Number(cogsPercentage.toFixed(1)),
    totalQuantity,
    totalLines: lines.length,
    totalUpcharges: Number(totalUpcharges.toFixed(2)),
    printMinutes,
    printRevenue,
    pressRate,
    upchargePrice: (orderData.upchargePrice === '' || orderData.upchargePrice == null || Number.isNaN(parseFloat(orderData.upchargePrice))) ? 2 : parseFloat(orderData.upchargePrice),
    salesTax: Number(salesTax.toFixed(2))
  };
};

const generateUniqueId = (() => {
  let counter = 0;
  return (prefix = '') => {
    counter += 1;
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}-${counter}`;
  };
})();

const NewOrder2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { facilityId: activeFacilityId } = useFacility();
  const today = new Date();
  const defaultDueDate = (() => {
    let date = new Date(today);
    let addedDays = 0;
    while (addedDays < 8) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        addedDays++;
      }
    }
    return date;
  })();

  // Check if we're coming from a reorder
  const reorderData = location.state?.fromReorder ? location.state : null;

  // Core state
  const [lines, setLines] = useState(
    reorderData?.lines || [{
      id: generateUniqueId('line'),
      isExpanded: true,
      type: LINE_TYPES.SCREEN_PRINT, // Initialize with default type
      design: '',
      style: '',
      color: '',
      sizeType: 'ADULT',
      sizes: Object.fromEntries(SIZES.ADULT.map(size => [size, ''])),
      inkCounts: { Front: 1, Back: 0, Center: 0 },
      inks: {},
      frontInkColors: [], // Initialize ink color arrays
      backInkColors: [],
      otherInkColors: [],
      pricePerPiece: '',
      cogs: '',
      mockupUrl: ''
    }]
  );

  const [orderData, setOrderData] = useState({
    customerName: reorderData?.customer?.client || '',
    customerId: reorderData?.customer?.id || '',
    projectManager: '',
    facilityId: activeFacilityId,
    startDate: today.toISOString().split('T')[0],
    dueDate: defaultDueDate.toISOString().split('T')[0],
    notes: '',
    fee1Type: '',
    fee1Quantity: '',
    fee1Price: '',
    fee2Type: '',
    fee2Quantity: '',
    fee2Price: '',
    upchargePrice: '2.00',
    discount: { type: 'fixed', amount: '' },
    additionalInstructions: {}
  });

  const [showFeesModal, setShowFeesModal] = useState(false);
  const [fees, setFees] = useState([
    { id: 1, type: '', quantity: '', price: '' },
    { id: 2, type: '', quantity: '', price: '' }
  ]);
  const [shippingDetails, setShippingDetails] = useState(null);

  // Search and selection state
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState(reorderData?.customer?.client || '');
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [projectManagers, setProjectManagers] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerTaxExempt, setCustomerTaxExempt] = useState(
    Boolean(reorderData?.customer?.taxExempt)
  );

  // Fetch customers on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      const q = query(collection(db, 'customers'), orderBy('client'));
      const querySnapshot = await getDocs(q);
      const customersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomers(customersData);
    };
    fetchCustomers();
  }, []);

  // Fetch project managers on mount
  useEffect(() => {
    const fetchProjectManagers = async () => {
      const q = query(
        collection(db, 'employees'),
        where('role', '==', EMPLOYEE_ROLES.PROJECT_MANAGER)
      );
      const querySnapshot = await getDocs(q);
      const managersData = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(manager => manager.active)
        .sort((a, b) => a.name.localeCompare(b.name));
      setProjectManagers(managersData);
    };
    fetchProjectManagers();
  }, []);

  // Customer search handler
  const handleCustomerSearch = (e) => {
    const query = e.target.value;
    setCustomerSearch(query);
    if (query.trim() === '') {
      setFilteredCustomers([]);
      return;
    }
    const filtered = customers.filter(customer =>
      customer.client.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCustomers(filtered);
  };

  // Customer selection handler
  const handleCustomerSelect = (customer) => {
    setOrderData(prev => ({
      ...prev,
      customerId: customer.id,
      customerName: customer.client
    }));
    setCustomerTaxExempt(customer.taxExempt === true);
    setCustomerSearch(customer.client);
    setFilteredCustomers([]);
  };

  // Save order handler
  const handleSave = async () => {
    try {
      if (isSubmitting) return;
      setIsSubmitting(true);

      // Get next PO number
      const poNumber = await getNextPoNumber();

      // Upload artwork files and update line items
      // This handles both legacy artworkFile and new files structure with blob URLs
      // Also handles production files with blob URLs
      const updatedLines = await Promise.all(lines.map(async (line) => {
        const designNameSanitized = (line.design || line.designName || 'design').replace(/[^a-zA-Z0-9_-]/g, '_');
        let mockupUrl = null;
        let fileToUpload = null;
        
        // ===== HANDLE MOCKUP FILES =====
        // Check for legacy artworkFile
        if (line.artworkFile) {
          fileToUpload = line.artworkFile;
        }
        // Check for File object stored with mockup (when blob URL was created)
        else if (line._mockupFile) {
          fileToUpload = line._mockupFile;
        }
        // Check for File object in files.mockup.file
        else if (line.files?.mockup?.file) {
          fileToUpload = line.files.mockup.file;
        }
        // Check if designFileUrl is a blob URL (needs to be uploaded)
        else if (line.designFileUrl && line.designFileUrl.startsWith('blob:')) {
          // Try to get File from files.mockup.file
          if (line.files?.mockup?.file) {
            fileToUpload = line.files.mockup.file;
          } else {
            console.warn('⚠️ Found blob URL but no File object to upload:', line.designName);
          }
        }
        
        // Upload mockup file if we have one
        if (fileToUpload) {
          const fileExtension = fileToUpload.name.split('.').pop() || 'png';
          const storageRef = ref(storage, `artwork/${poNumber}/${designNameSanitized}/Mockups/mockup.${fileExtension}`);
          await uploadBytes(storageRef, fileToUpload);
          mockupUrl = await getDownloadURL(storageRef);
          console.log('✅ Uploaded mockup to Firebase Storage:', mockupUrl);
        }
        
        // ===== HANDLE PRODUCTION FILES =====
        let updatedProductionFiles = line.files?.production || [];
        if (updatedProductionFiles.length > 0) {
          // Check for production files with blob URLs that need to be uploaded
          const productionUploadPromises = updatedProductionFiles.map(async (prodFile) => {
            // If this file has a blob URL and a File object, upload it
            if (prodFile.url && prodFile.url.startsWith('blob:') && prodFile.file) {
              try {
                // designNameSanitized is already sanitized, but ensure folder name is safe
                const typeFolder = (prodFile.type || 'Unassigned').replace(/[^a-zA-Z0-9_-]/g, '_');
                const storageRef = ref(storage, `artwork/${poNumber}/${designNameSanitized}/Production/${typeFolder}/${prodFile.name}`);
                await uploadBytes(storageRef, prodFile.file);
                const firebaseUrl = await getDownloadURL(storageRef);
                console.log('✅ Uploaded production file to Firebase Storage:', prodFile.name);
                
                // Return updated file object with Firebase URL
                return {
                  name: prodFile.name,
                  url: firebaseUrl,
                  type: prodFile.type || 'unassigned'
                  // Remove file property - it's not needed in Firestore
                };
              } catch (error) {
                console.error('❌ Error uploading production file:', prodFile.name, error);
                // Return original file (will be filtered out by normalizeFilesForFirestore)
                return prodFile;
              }
            }
            // If already a Firebase URL, keep it as-is (but remove File object if present)
            return {
              name: prodFile.name,
              url: prodFile.url,
              type: prodFile.type || 'unassigned'
              // Remove file property if present
            };
          });
          
          updatedProductionFiles = await Promise.all(productionUploadPromises);
        }
        
        // Build updated files object
        const updatedFiles = {
          ...(line.files || {}),
          ...(mockupUrl && {
            mockup: {
              ...(line.files?.mockup || {}),
              url: mockupUrl,
              fileName: fileToUpload?.name || line.files?.mockup?.fileName
              // Remove file property - it's not needed in Firestore
            }
          }),
          ...(updatedProductionFiles.length > 0 && {
            production: updatedProductionFiles
          })
        };
        
        // Return updated line with Firebase Storage URLs
        const cleanedLine = {
          ...line,
          ...(getPersistableMockupUrl(mockupUrl) && { designFileUrl: getPersistableMockupUrl(mockupUrl) }),
          files: Object.keys(updatedFiles).length > 0 ? updatedFiles : undefined,
          // Remove temporary File objects
          _mockupFile: undefined
        };
        
        // Clean up temporary File objects from nested structures
        if (cleanedLine.files?.mockup?.file) {
          delete cleanedLine.files.mockup.file;
        }
        
        return cleanedLine;
      }));

      const providedGoods = orderData.additionalInstructions?.providedGoods === true;
      const isSample = orderData.additionalInstructions?.sample === true;
      const pricing = calculatePricing(lines, orderData, fees, customerTaxExempt);
      const taxExempt = resolveOrderTaxExemptFlag({
        order: orderData,
        customer: customerTaxExempt ? { taxExempt: true } : null,
      });
      const orderPayload = {
        poNumber,
        projectManager: orderData.projectManager,
        facilityId: resolveFacilityId(orderData.facilityId || activeFacilityId),
        customerId: orderData.customerId,
        customerName: orderData.customerName,
        taxExempt,
        startDate: serverTimestamp(),
        dueDate: orderData.dueDate,
        notes: orderData.notes || '',
        status: ORDER_STATUSES.NEW,
        createdAt: serverTimestamp(),
        readyToOrder: false,
        readyToOrderDate: null,
        printedLines: 0,
        printedQuantity: 0,
        totalLines: lines.length,
        lineRefs: [],
        providedGoods,
        ...(isSample && { sample: true }),
        ...(orderData.additionalInstructions && { additionalInstructions: orderData.additionalInstructions }),
        ...(orderData.discount && { discount: orderData.discount }),
        ...(Array.isArray(fees) && { fees }),
        ...(shippingDetails && { shippingDetails }),
        // When Provided Goods: force 2XL upcharge to $0 and ensure backend creates order as ORDERED
        ...(providedGoods && { upchargePrice: 0 }),
        lines: updatedLines.map((line, index) => ({
          designName: line.design || '',
          garmentId: line.style || '',
          garmentColor: line.color || '',
          sizes: Object.fromEntries(
            Object.entries(line.sizes || {}).map(([size, qty]) => [size, Number(qty) || 0])
          ),
          pricePerPiece: Number(line.price) || 0,
          cogs: Number(line.cogs) || 0,
          status: providedGoods ? LINE_STATUSES.ORDERED : LINE_STATUSES.PENDING,
          frontInkColors: line.frontInkColors || [],
          backInkColors: line.backInkColors || [],
          otherInkColors: line.otherInkColors || [],
          printLocations: line.printLocations || [],
          sizeType: line.sizeType || 'unisex',
          lineNumber: index + 1,
          type: line.type || LINE_TYPES.SCREEN_PRINT, // Use line's actual type
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          designFileUrl: getPersistableMockupUrl(line.designFileUrl) || null,
          ...(line.pressSetup && { pressSetup: line.pressSetup })
        })),
        ...pricing
      };
      if (providedGoods) orderPayload.upchargePrice = 0;

      const result = await addOrderToDb(orderPayload);

      if (result.success) {
        if (orderData.customerId) {
          await Promise.all(
            updatedLines.map((line) =>
              registerDesignMockupFromLine({
                customerId: orderData.customerId,
                line,
                orderId: result.orderId,
                orderNumber: poNumber
              })
            )
          );
        }

        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          setIsSubmitting(false);
          navigate('/orders');
        }, 2000);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Error creating order: ' + error.message);
      setIsSubmitting(false);
    }
  };

  // Add new customer handler
  const handleAddNewCustomer = async (newCustomer) => {
    try {
      const docRef = await addDoc(collection(db, 'customers'), enrichCustomerDocForCreate(newCustomer));
      const customerWithId = { id: docRef.id, ...newCustomer };
      setCustomers(prev => [...prev, customerWithId]);
      handleCustomerSelect(customerWithId);
      setShowAddCustomer(false);
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Error adding customer. Please try again.');
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        <HeaderSection>
          <CustomerSearchContainer>
            <TextField
              fullWidth
              label="Search Customers"
              value={customerSearch}
              onChange={handleCustomerSearch}
              InputProps={{
                sx: { textTransform: 'uppercase' }
              }}
            />
            {filteredCustomers.length > 0 && (
              <SearchResults>
                {filteredCustomers.map(customer => (
                  <SearchResultItem
                    key={customer.id}
                    onClick={() => handleCustomerSelect(customer)}
                  >
                    <Typography variant="body2">
                      {customer.client}
                    </Typography>
                  </SearchResultItem>
                ))}
              </SearchResults>
            )}
            {customerSearch && !filteredCustomers.length && (
              <SearchResults>
                <SearchResultItem
                  onClick={() => setShowAddCustomer(true)}
                  sx={{ color: 'primary.main' }}
                >
                  <Typography variant="body2">
                    + Add New Customer
                  </Typography>
                </SearchResultItem>
              </SearchResults>
            )}
          </CustomerSearchContainer>

          <FormControl fullWidth size="small">
            <InputLabel>Project Manager</InputLabel>
            <Select
              value={orderData.projectManager}
              onChange={(e) => setOrderData(prev => ({ ...prev, projectManager: e.target.value }))}
              label="Project Manager"
            >
              <MenuItem value="">
                <em>Select PM</em>
              </MenuItem>
              {projectManagers.map(pm => (
                <MenuItem key={pm.id} value={pm.name}>
                  {pm.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Production Facility</InputLabel>
            <Select
              value={resolveFacilityId(orderData.facilityId || activeFacilityId)}
              onChange={(e) => setOrderData((prev) => ({ ...prev, facilityId: e.target.value }))}
              label="Production Facility"
            >
              {FACILITIES.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={orderData.startDate}
                onChange={(e) => setOrderData(prev => ({ ...prev, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={orderData.dueDate}
                onChange={(e) => setOrderData(prev => ({ ...prev, dueDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </HeaderSection>

        <LinesSection>
          {lines.map((line, index) => (
            <Box key={line.id} sx={{ mb: 2 }}>
              <LineItem2
                lineData={line}
                index={index}
                customerId={orderData.customerId}
                onUpdate={(updatedLine) => {
                  setLines(prevLines => {
                    const newLines = [...prevLines];
                    newLines[index] = {
                      ...newLines[index],
                      ...updatedLine,
                      id: newLines[index].id // Preserve the original ID
                    };
                    return newLines;
                  });
                }}
                onDelete={() => {
                  setLines(prevLines => {
                    const newLines = [...prevLines];
                    newLines.splice(index, 1);
                    return newLines;
                  });
                }}
              />
            </Box>
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                setLines(prevLines => [...prevLines, {
                  id: generateUniqueId('line'),
                  isExpanded: true,
                  type: LINE_TYPES.SCREEN_PRINT, // Initialize with default type
                  design: '',
                  style: '',
                  color: '',
                  sizeType: 'ADULT',
                  sizes: Object.fromEntries(SIZES.ADULT.map(size => [size, ''])),
                  inkCounts: { Front: 1, Back: 0, Center: 0 },
                  inks: {},
                  frontInkColors: [], // Initialize ink color arrays
                  backInkColors: [],
                  otherInkColors: [],
                  pricePerPiece: '',
                  cogs: '',
                  mockupUrl: ''
                }]);
              }}
              startIcon={<AddIcon />}
            >
              Add Line
            </Button>
          </Box>
        </LinesSection>

        <FooterSection>
          {/* Notes Column */}
          <NotesFooterBox>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Order Notes..."
              value={orderData.notes}
              onChange={(e) => setOrderData(prev => ({ ...prev, notes: e.target.value }))}
              InputProps={{
                sx: { 
                  textTransform: 'uppercase',
                  height: '100%',
                  background: 'transparent',
                  '& textarea': {
                    height: '100% !important'
                  }
                }
              }}
              sx={{ flex: 1 }}
            />
          </NotesFooterBox>

          {/* +2XL Upcharge Column */}
          <UpchargeFooterBox>
            <div style={{ padding: '4px', fontWeight: 600, fontSize: '0.85em' }}>+2XL Upcharge</div>
            <FooterRow>
              <span className="label">QTY:</span>
              <input
                type="text"
                value={calculate2XLPlus(lines) || 0}
                readOnly
                style={{ backgroundColor: 'rgba(54, 54, 54, 0.5)' }}
              />
            </FooterRow>
            <FooterRow>
              <span className="label">$:</span>
              <input
                type="text"
                value={orderData.upchargePrice === '' || orderData.upchargePrice == null ? '2.00' : orderData.upchargePrice}
                onChange={(e) => setOrderData({...orderData, upchargePrice: e.target.value})}
              />
            </FooterRow>
            <FooterRow>
              <span className="label">Total:</span>
              <input
                type="text"
                value={((calculate2XLPlus(lines) || 0) * (parseFloat(orderData.upchargePrice) || 0)).toFixed(2)}
                readOnly
                style={{ backgroundColor: 'rgba(54, 54, 54, 0.5)' }}
              />
            </FooterRow>
          </UpchargeFooterBox>

          {/* Fees, Discounts & Instructions Column */}
          <FeesFooterBox>
            <div className="header">Fees, Discounts & Instructions</div>
            <div className="summary">
              {/* Fees */}
              {fees.filter((fee) => fee.type && fee.quantity && fee.price).map((fee) => (
                <div className="fee-line" key={fee.id}>
                  <span className="fee-type">{fee.type}</span>
                  <span className="fee-amount">${(Number(fee.quantity || 0) * Number(fee.price || 0)).toFixed(2)}</span>
                </div>
              ))}
              {/* Discount */}
              {orderData.discount && orderData.discount.amount && (
                <div className="fee-line">
                  <span className="fee-type">Discount ({orderData.discount.type === 'percent' ? `${orderData.discount.amount}%` : 'Fixed'})</span>
                  <span className="fee-amount" style={{ color: '#FF6B6B' }}>
                    -${calculatePricing(lines, orderData, fees).discountAmount.toFixed(2)}
                  </span>
                </div>
              )}
              {/* Instructions */}
              {orderData.additionalInstructions && (
                <div className="instructions">
                  {orderData.additionalInstructions.shipping && <span className="instruction-tag">Shipping</span>}
                  {orderData.additionalInstructions.sorting && <span className="instruction-tag">Sorting</span>}
                  {orderData.additionalInstructions.folding && <span className="instruction-tag">Folding</span>}
                  {orderData.additionalInstructions.bagging && <span className="instruction-tag">Bagging</span>}
                  {orderData.additionalInstructions.fulfillment && <span className="instruction-tag">Fulfillment</span>}
                  {orderData.additionalInstructions.neckTag && <span className="instruction-tag">Neck Tag</span>}
                  {orderData.additionalInstructions.taxExempt && <span className="instruction-tag" style={{ background: '#2196F3' }}>Tax Exempt (Order)</span>}
                  {customerTaxExempt && !orderData.additionalInstructions?.taxExempt && (
                    <span className="instruction-tag" style={{ background: '#2196F3' }}>Tax Exempt (Customer)</span>
                  )}
                  {orderData.additionalInstructions.designWorkOnly && <span className="instruction-tag" style={{ background: '#9C27B0' }}>Design Only</span>}
                  {orderData.additionalInstructions.doNotBill && <span className="instruction-tag" style={{ background: '#f44336' }}>Do Not Bill</span>}
                  {orderData.additionalInstructions.followUpEmail && <span className="instruction-tag" style={{ background: '#2196F3' }}>Follow-Up Email</span>}
                  {orderData.additionalInstructions.instagramWorthy && <span className="instruction-tag">Instagram Worthy</span>}
                  {orderData.additionalInstructions.providedGoods && <span className="instruction-tag" style={{ background: '#5B9BD5' }}>Provided Goods</span>}
                  {orderData.additionalInstructions.sample && <span className="instruction-tag" style={{ background: '#8E6BBE' }}>Sample</span>}
                  {orderData.additionalInstructions.other && <span className="instruction-tag" style={{ background: '#607D8B' }}>{orderData.additionalInstructions.other}</span>}
                </div>
              )}
              {/* Empty state */}
              {fees.filter((fee) => fee.type && fee.quantity && fee.price).length === 0 && !(orderData.discount && orderData.discount.amount) && !hasActiveInstructions(orderData.additionalInstructions) && (
                <span style={{ fontStyle: 'italic' }}>No fees, discounts, or instructions</span>
              )}
            </div>
            <EditFeesBtn onClick={() => setShowFeesModal(true)}>
              Edit
            </EditFeesBtn>
          </FeesFooterBox>

          {/* Totals Column */}
          <TotalsFooterBox>
            <div className="header">TOTALS</div>
            <FooterRow>
              <span className="label">PCS:</span>
              <input type="text" value={calculateTotalPieces(lines)} readOnly />
            </FooterRow>
            <FooterRow>
              <span className="label">$:</span>
              <input
                type="text"
                value={(() => {
                  const pricing = calculatePricing(lines, orderData, fees, customerTaxExempt);
                  const orderTotal = pricing.orderTotal;
                  const exempt = resolveTaxExempt({
                    order: orderData,
                    customer: customerTaxExempt ? { taxExempt: true } : null,
                  });
                  const tax = exempt ? 0 : pricing.salesTax;
                  return tax > 0 ? `$${orderTotal.toFixed(2)} + $${tax.toFixed(2)}/tax` : `$${orderTotal.toFixed(2)}`;
                })()}
                readOnly
              />
            </FooterRow>
            <FooterRow>
              <span className="label">Stats:</span>
              <input 
                type="text" 
                value={`${calculatePricing(lines, orderData, fees).cogsPercentage.toFixed(1)}% / ${formatMinutes(calculatePricing(lines, orderData, fees).printMinutes)} ${(() => {
                  const rate = calculatePricing(lines, orderData, fees).pressRate;
                  if (rate >= 350) return '🟢';
                  if (rate >= 300) return '🟡';
                  return '🔴';
                })()}`}
                readOnly 
              />
            </FooterRow>
            <Button
              variant="contained"
              fullWidth
              size="small"
              onClick={handleSave}
              disabled={isSubmitting}
              sx={{ mt: 'auto' }}
            >
              {isSubmitting ? 'Creating...' : 'Create Order'}
            </Button>
          </TotalsFooterBox>
        </FooterSection>

        {/* Fees Modal */}
        <FeesModal
          isOpen={showFeesModal}
          onClose={() => setShowFeesModal(false)}
          enableShipping
          shippingContext={{
            customerName: orderData.customerName || '',
            orderReference: orderData.poNumber ? String(orderData.poNumber) : '',
            customerReference: orderData.customerId || ''
          }}
          initialData={{
            fees,
            discount: orderData.discount || { type: 'fixed', amount: '' },
            additionalInstructions: orderData.additionalInstructions || {},
            upchargePrice: orderData.upchargePrice,
            customerPoNumber: orderData.customerPoNumber || '',
            shippingDetails
          }}
          onSave={(feesData) => {
            setFees(feesData.fees || []);
            setShippingDetails(feesData.shippingDetails || null);
            setOrderData(prev => ({
              ...prev,
              discount: feesData.discount,
              additionalInstructions: feesData.additionalInstructions,
              ...('upchargePrice' in feesData && { upchargePrice: feesData.upchargePrice }),
              ...('customerPoNumber' in feesData && { customerPoNumber: feesData.customerPoNumber })
            }));
          }}
        />

        {showSuccess && <SuccessAnimation onComplete={() => setShowSuccess(false)} />}
        
        <AddCustomer
          isOpen={showAddCustomer}
          onClose={() => setShowAddCustomer(false)}
          onAdd={handleAddNewCustomer}
          initialData={{ client: customerSearch }}
        />
      </PageContainer>
    </ThemeProvider>
  );
};

export default NewOrder2;
